/**
 * The kinds of exercise the create dialog picks from.
 *
 * Backed by TanStack Query rather than fetched fresh with every opening of the
 * dialog: the list barely changes, so a workspace that has opened it once this
 * session gets it back instantly on every later opening, and only a quiet
 * background read checks whether it has moved. The same arrangement
 * `useMealPlanTypes` makes, for the same reason.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchExerciseTypes } from "../services/exerciseTypesService";
import type { ExerciseTypeOption } from "../types/exercisePlans.types";

export type ExerciseTypesStatus = "loading" | "ready" | "failed";

export interface ExerciseTypesState {
  options: ExerciseTypeOption[];
  status: ExerciseTypesStatus;
}

const NO_TYPES: ExerciseTypeOption[] = [];

export const EXERCISE_TYPES_QUERY_KEY = ["exercises", "types"] as const;

export function useExerciseTypes(): ExerciseTypesState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: EXERCISE_TYPES_QUERY_KEY,
    queryFn: ({ signal }) => fetchExerciseTypes(getToken, signal),
  });

  useEffect(() => {
    if (query.isError)
      console.error("Exercise types failed to load:", query.error);
  }, [query.isError, query.error]);

  return {
    options: query.data ?? NO_TYPES,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
  };
}
